import styled from '@emotion/styled';
import { ActionIcon, Box, Popover, Text } from '@mantine/core';
import { AnchorButton, PrimitiveTypeIcon } from '@root/Design/Primitives';
import { useToggle } from '@root/Services/EventEmitter';
import { forwardRef, ReactNode, useCallback, useMemo } from 'react';
import { Square } from 'tabler-icons-react';

const sortedSystemIconNames = [
    /* Generic */
    'tag',
    'badge',
    'bookmark',
    /* Purpose */
    'coin',
    'security',
    'certificate',
    'location',
    'time',
    'user',
    /* Meta */
    'flag',
    'experimental',
    'question',
    'system',
    /* Directive */
    'lock',
    'info',
    'warn',
    'alert',
    'forbid',
] as const;
type SystemIconName = (typeof sortedSystemIconNames)[number];
type SystemIconExternalMapping = { lookup: Record<SystemIconName, string>; transform: (externalName: string) => string };

const tablerMapping: SystemIconExternalMapping = {
    transform: (name) => `ti ti-${name}`,
    lookup: {
        /* Generic */
        tag: 'tag',
        badge: 'badge',
        bookmark: 'bookmark',
        /* Purpose */
        coin: 'coin',
        security: 'shield',
        certificate: 'award',
        location: 'map-pin',
        time: 'clock',
        user: 'user',
        /* Meta */
        flag: 'flag',
        experimental: 'flask',
        question: 'question-mark',
        system: 'cloud-lock',
        /* Directive */
        lock: 'lock',
        info: 'message',
        warn: 'alert-circle',
        alert: 'alert-triangle',
        forbid: 'forbid',
    },
};

const iconMapping = tablerMapping;

const systemIconColors = ['blue', 'green', 'gold', 'orange', 'red', 'gray'] as const;
type SystemIconColorName = (typeof systemIconColors)[number];
const iconColorHexMapping: Record<SystemIconColorName, string> = {
    blue: '#0075C1',
    green: '#039855',
    gold: '#cc9200',
    orange: '#DC6803',
    red: '#D92D20',
    gray: '#667085',
};

const systemIconValues = sortedSystemIconNames.flatMap((n) => systemIconColors.map((c) => ({ Name: n, Color: c })));

const mapIconValue = (icon?: IconValue | null) => {
    const systemName = (icon?.Name ?? 'question') as SystemIconName;
    const systemColor = (icon?.Color ?? 'gray') as SystemIconColorName;
    const className = iconMapping.transform(iconMapping.lookup[systemName] ?? systemName);
    const color = iconColorHexMapping[systemColor] ?? systemColor;
    return { className, color };
};

export interface IconValue {
    Name?: string | null;
    Color?: string | null;
}

interface IIconOptionListProps {
    value?: IconValue;
    onChange: (value?: IconValue) => void;
    options?: IconValue[];
}
export function IconOptionList(props: IIconOptionListProps) {
    const { onChange, options = systemIconValues, value } = props;
    const items = useMemo(
        () =>
            systemIconValues.map((o) => ({
                mode: o.Color === value?.Color && o.Name === value?.Name ? ('selected' as const) : ('none' as const),
                ...mapIconValue(o),
                onClick: () => onChange(o),
            })),
        [options, value, onChange]
    );
    const noneSelected = !value?.Color || !value.Name;
    const selectNone = useCallback(() => onChange(undefined), [onChange]);
    return (
        <IconListLayout colorCount={systemIconColors.length}>
            {items.map((o, i) => (
                <IconOptionItem key={i} {...o}></IconOptionItem>
            ))}
            <IconListFullWidthItem onClick={selectNone} mode={noneSelected ? 'selected' : 'none'}>
                <Text color="dimmed" align="center">
                    None
                </Text>
            </IconListFullWidthItem>
        </IconListLayout>
    );
}

interface IIconPickerProps extends IIconOptionListProps {
    noneIcon?: ReactNode;
}
export function IconPicker(props: IIconPickerProps) {
    const { onChange, noneIcon, value } = props;
    const [opened, { close, toggle }] = useToggle(false);
    const handleChange: IIconPickerProps['onChange'] = useCallback(
        (value) => {
            onChange(value);
            close();
        },
        [onChange, close]
    );

    return (
        <Popover withinPortal opened={opened} onClose={close} shadow="md" position="bottom-start" offset={0} withArrow radius="sm">
            <Popover.Target>
                <div onClick={toggle}>
                    {!value ? (
                        noneIcon ?? <AnchorButton color="gray" onClick={() => {}} size="sm" text="None" icon={<Square stroke="#0008" />} />
                    ) : (
                        <ActionIcon>
                            <UdIcon withBackground style={{ fontSize: 24 }} icon={value}></UdIcon>
                        </ActionIcon>
                    )}
                </div>
            </Popover.Target>
            <Popover.Dropdown p={0}>
                <Box p={6} sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <IconOptionList {...props} onChange={handleChange} />
                </Box>
            </Popover.Dropdown>
        </Popover>
    );
}

const IconOptionItem = styled(PrimitiveTypeIcon)<{ mode: 'none' | 'selected' }>`
    &:hover {
        transform: scale(1.25);
        transform-origin: center;
        transition: transform 100ms ease;
        outline: solid 2px ${(p) => p.color};
    }

    outline: solid ${(p) => (p.mode === 'selected' ? 2 : 0)}px ${(p) => p.color};
`;
const IconListLayout = styled.div<{ colorCount: number }>`
    display: grid;
    grid-template-columns: repeat(${(p) => p.colorCount}, 24px);
    grid-auto-rows: 24px;
    gap: 6px;
    > i,
    > div {
        cursor: pointer;
    }
    > i {
        font-size: 24px;
    }
`;
const IconListFullWidthItem = styled.div<{ mode: 'none' | 'selected' }>`
    grid-column: 1 / -1;
    height: 24px;
    width: 100%;
    outline: solid ${(p) => (p.mode === 'selected' ? 2 : 0)}px ${(p) => p.theme.colors.primary[3]};
    border-radius: 4px;
    &:hover {
        outline: solid 2px ${(p) => p.theme.colors.primary[3]};
    }
`;

/** User-defined icon supporting user configuration of icons */
export const UdIcon = forwardRef<
    HTMLElement,
    React.ComponentPropsWithoutRef<'i'> & { icon?: IconValue | null; size?: number; withBackground?: boolean }
>(({ icon, withBackground, size, ...props }, ref) => {
    const iconProps = mapIconValue(icon);
    return (
        <PrimitiveTypeIcon
            ref={ref}
            className={iconProps.className}
            disableBackground={!withBackground}
            style={{ fontSize: size ?? 16 }}
            color={iconProps.color}
            {...props}
        />
    );
});
